/* ==========================================================================
   nma-carousel.js — carrusel reutilizable con scroll-snap nativo
   Ref: bactoboost-spec-desarrollo-v2.2.md §5.4

   Sin librerías (regla 1.4). El desplazamiento lo hace el navegador: acá solo
   viven las flechas y su estado. Eso es lo que hace que el teclado funcione
   solo — al tabular a una tarjeta el navegador la trae a la vista.

   Uso:
     <nma-carousel class="nma-car">
       <button data-prev>…</button>
       <div class="nma-car-track">…tarjetas…</div>
       <button data-next>…</button>
     </nma-carousel>

   Antes esto soportaba además una "pista ajena": con `data-track` la pista
   podía ser markup que dibujaba `mag-product-reviews`, con un MutationObserver
   esperándola y un reparador de `aria-label` para sus enlaces sin nombre. Todo
   eso salió en la migración a Judge.me — la sección de reseñas ya no envuelve
   markup de terceros. Quedan dos consumidores, colecciones y productos, y los
   dos usan la pista propia.
   ========================================================================== */

/* El archivo se envuelve en una función y sale temprano si ya se ejecutó.
   Tres secciones lo cargan con la misma etiqueta <script src>, y el navegador
   ejecuta cada una: una `class` en el ámbito global lanzaría
   "Identifier 'NmaCarousel' has already been declared" en la segunda y la
   tercera. Detectado por la auditoría errors-in-console de Lighthouse el
   6 de agosto de 2026 — ver CUSTOM.md §7.4. */
(() => {
  if (customElements.get('nma-carousel')) return;

  class NmaCarousel extends HTMLElement {
    connectedCallback() {
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.track = this.querySelector('.nma-car-track');
      this.iniciar();
    }

    iniciar() {
      if (!this.track) return;
      this.prev?.addEventListener('click', () => this.step(-1));
      this.next?.addEventListener('click', () => this.step(1));

      this.onScroll = () => this.updateButtons();
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      // El ancho de tarjeta cambia con el viewport, y el track puede dejar de
      // desbordar: sin esto las flechas quedan habilitadas sobre nada que scrollear.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateButtons());
        this.resizeObserver.observe(this.track);
      }

      this.updateButtons();
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();
    }

    /* Se llama step y no scrollBy para no colisionar con Element.scrollBy. */
    step(dir) {
      const card = this.track.firstElementChild;
      if (!card) return;

      const gap = parseInt(getComputedStyle(this.track).gap) || 0;
      const salto = (card.offsetWidth + gap) * 2;

      this.track.scrollBy({
        left: dir * salto,
        behavior: this.prefiereMenosMovimiento ? 'auto' : 'smooth',
      });
    }

    updateButtons() {
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;

      // Margen de 2px: los navegadores redondean scrollLeft y el último tramo
      // puede quedar en 0.5px, dejando la flecha habilitada para siempre.
      if (this.prev) this.prev.disabled = t.scrollLeft <= 2;
      if (this.next) this.next.disabled = t.scrollLeft >= max - 2;

      // Sin desborde no hay nada que recorrer. Las dos apagadas, y una clase
      // en el host para que el CSS centre las tarjetas y esconda las flechas.
      // Con el catálogo consolidado este es el caso normal, no el raro: tres
      // tarjetas en un track de ancho completo quedaban pegadas a la izquierda
      // con medio ancho vacío a la derecha, y dos flechas grises sobre nada.
      const quieto = max <= 2;
      this.classList.toggle('nma-car--quieto', quieto);

      if (quieto && this.prev && this.next) {
        this.prev.disabled = true;
        this.next.disabled = true;
      }
    }

    get prefiereMenosMovimiento() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('nma-carousel', NmaCarousel);
})();
